import { useEffect, useState } from 'react';

export const accountSections = ['apps', 'perfil', 'metodos', 'sesions', 'permisos', 'identidades'] as const;
export const adminSections = ['identidades', 'realms', 'aplicacions', 'acceso', 'campos', 'roles', 'grupos', 'scopes', 'auditoria', 'invitacions', 'chaves', 'google-oauth'] as const;
export const realmAdminSections = ['membros', 'admission', 'aplicacions', 'invitacions', 'bloqueos', 'admins', 'campos', 'roles', 'grupos', 'scopes', 'auditoria', 'chaves'] as const;

export type AccountSection = typeof accountSections[number];
export type AdminSection = typeof adminSections[number];
export type RealmAdminSection = typeof realmAdminSections[number];

export type AppRoute =
  | { kind: 'interaction'; realmSlug: string; uid: string }
  | { kind: 'account'; section: AccountSection }
  | { kind: 'global-admin'; section: AdminSection; applicationId?: string }
  | { kind: 'realm-admin'; section: RealmAdminSection; realmId?: string; identityId?: string }
  | { kind: 'invitation' }
  | { kind: 'bootstrap' }
  | { kind: 'google-continue' }
  | { kind: 'not-found' };

function oneOf<T extends string>(value: string | undefined, values: readonly T[], fallback: T): T {
  return value && values.includes(value as T) ? value as T : fallback;
}

export function resolveRoute(pathname: string): AppRoute {
  const parts = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (parts[0] === 'realms' && parts[2] === 'interaction' && parts[1] && parts[3]) return { kind: 'interaction', realmSlug: decodeURIComponent(parts[1]), uid: decodeURIComponent(parts[3]) };
  if (parts[0] === 'conta') return { kind: 'account', section: oneOf(parts[1], accountSections, 'apps') };
  if (parts[0] === 'admin' && parts[1] === 'realms' && parts[2]) {
    if (parts[3] === 'membros' && parts[4] && parts.length === 5) return { kind: 'realm-admin', realmId: decodeURIComponent(parts[2]), section: 'membros', identityId: decodeURIComponent(parts[4]) };
    if (parts.length > 4) return { kind: 'not-found' };
    return { kind: 'realm-admin', realmId: decodeURIComponent(parts[2]), section: oneOf(parts[3], realmAdminSections, 'membros') };
  }
  if (parts[0] === 'admin') {
    const legacyRealmSections: Record<string, RealmAdminSection> = { acceso: 'admission', campos: 'campos', roles: 'roles', grupos: 'grupos', scopes: 'scopes', auditoria: 'auditoria', invitacions: 'invitacions', chaves: 'chaves' };
    const legacySection = legacyRealmSections[parts[1] ?? ''];
    if (legacySection) return { kind: 'realm-admin', section: legacySection };
    const section = oneOf(parts[1], adminSections, 'identidades');
    return section === 'aplicacions' && parts[2] ? { kind: 'global-admin', section, applicationId: decodeURIComponent(parts[2]) } : { kind: 'global-admin', section };
  }
  // Legacy realm URLs remain reachable, but intentionally do not select a realm.
  if (parts[0] === 'realm-admin') return { kind: 'realm-admin', section: oneOf(parts[1], realmAdminSections, 'membros') };
  if (parts[0] === 'convite') return { kind: 'invitation' };
  if (parts[0] === 'bootstrap' && parts.length === 1) return { kind: 'bootstrap' };
  if (parts[0] === 'auth' && parts[1] === 'google' && parts[2] === 'continue' && parts.length === 3) return { kind: 'google-continue' };
  if (parts.length === 0) return { kind: 'account', section: 'apps' };
  return { kind: 'not-found' };
}

export function navigate(to: string): void {
  window.history.pushState({}, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo?.({ top: 0, behavior: 'instant' });
}

export function useRoute(): AppRoute {
  const [route, setRoute] = useState(() => resolveRoute(window.location.pathname));
  useEffect(() => {
    const update = () => setRoute(resolveRoute(window.location.pathname));
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);
  return route;
}
